import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BuildingIcon, UserIcon, CheckCircleIcon, ShieldCheckIcon, Loader2Icon, ShareIcon, ArrowLeftIcon, DeleteIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface TransferMoneyProps {
  navigate: (screen: string) => void;
}
type Step = 'details' | 'confirm' | 'pin' | 'processing' | 'success';
export function TransferMoney({
  navigate
}: TransferMoneyProps) {
  const [step, setStep] = useState<Step>('details');
  const [accountNo, setAccountNo] = useState('');
  const [amount, setAmount] = useState('');
  const [bank, setBank] = useState('');
  const [narration, setNarration] = useState('');
  const [pin, setPin] = useState('');
  const balance = 2450000;
  const numAmount = amount ? parseInt(amount.replace(/,/g, '')) : 0;
  const fee = 10;
  const totalDebit = numAmount + fee;
  const isResolved = accountNo.length === 10 && bank !== '';
  const recipientName = 'John Doe';
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val) {
      setAmount(parseInt(val).toLocaleString('en-US'));
    } else {
      setAmount('');
    }
  };
  const handlePinPress = (digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);
      if (newPin.length === 4) {
        setStep('processing');
      }
    }
  };
  const handleDeletePin = () => {
    setPin(pin.slice(0, -1));
  };
  useEffect(() => {
    if (step === 'processing') {
      const timer = setTimeout(() => {
        setStep('success');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  const handleBack = () => {
    if (step === 'details') navigate('wallet');else if (step === 'confirm') setStep('details');else if (step === 'pin') {
      setPin('');
      setStep('confirm');
    }
  };
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  return <div className="flex-1 overflow-hidden bg-slate-50 flex flex-col h-full relative" data-id="element-2963">
      {step !== 'processing' && step !== 'success' && <TopBar title={step === 'pin' ? 'Enter PIN' : 'Send Money'} showBack onBack={handleBack} data-id="element-2964" />}

      <div className="flex-1 relative overflow-hidden" data-id="element-2965">
        <AnimatePresence mode="wait" data-id="element-2966">
          {step === 'details' && <motion.div key="details" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-2967">
              {/* Recipient Section */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-2968">
                <h3 className="text-sm font-bold text-[#0D3B3B] mb-4 flex items-center gap-2" data-id="element-2969">
                  <UserIcon className="w-4 h-4 text-gray-400" data-id="element-2970" /> Recipient
                  Details
                </h3>

                <div className="space-y-4" data-id="element-2971">
                  <div data-id="element-2972">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2973">
                      Select Bank
                    </label>
                    <div className="relative" data-id="element-2974">
                      <BuildingIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2975" />
                      <select value={bank} onChange={e => setBank(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 pl-12 pr-4 text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] appearance-none" data-id="element-2976">
                        <option value="" disabled data-id="element-2977">
                          Choose a bank...
                        </option>
                        <option value="gtb" data-id="element-2978">GTBank</option>
                        <option value="access" data-id="element-2979">Access Bank</option>
                        <option value="first" data-id="element-2980">First Bank</option>
                        <option value="uba" data-id="element-2981">UBA</option>
                        <option value="zenith" data-id="element-2982">Zenith Bank</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-2983">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2984">
                      Account Number
                    </label>
                    <input type="text" maxLength={10} value={accountNo} onChange={e => setAccountNo(e.target.value.replace(/\D/g, ''))} placeholder="0000000000" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3.5 px-4 text-sm font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B]" data-id="element-2985" />
                  </div>

                  {isResolved && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center gap-3" data-id="element-2986">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-2987" />
                      <div data-id="element-2988">
                        <p className="text-xs font-bold text-emerald-800" data-id="element-2989">
                          {recipientName}
                        </p>
                        <p className="text-[10px] font-medium text-emerald-600" data-id="element-2990">
                          Account verified
                        </p>
                      </div>
                    </motion.div>}
                </div>
              </div>

              {/* Amount Section */}
              <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 mb-6" data-id="element-2991">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2992">
                  Amount to Send
                </label>
                <div className="relative mb-2" data-id="element-2993">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-lg" data-id="element-2994">
                    ₦
                  </span>
                  <input type="text" value={amount} onChange={handleAmountChange} placeholder="0" className="w-full bg-gray-50 border border-gray-200 rounded-xl py-4 pl-10 pr-4 text-xl font-bold text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B]" data-id="element-2995" />
                </div>
                <div className="flex justify-between items-center text-xs" data-id="element-2996">
                  <span className="text-gray-500 font-medium" data-id="element-2997">
                    Available Balance:
                  </span>
                  <span className="font-bold text-emerald-600" data-id="element-2998">
                    {formatNaira(balance)}
                  </span>
                </div>
              </div>

              {/* Note Section */}
              <div className="mb-8" data-id="element-2999">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 px-1" data-id="element-3000">
                  Narration (Optional)
                </label>
                <input type="text" value={narration} onChange={e => setNarration(e.target.value)} placeholder="What's this for?" className="w-full bg-white border border-gray-200 rounded-xl py-3.5 px-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#0D3B3B] shadow-sm" data-id="element-3001" />
              </div>

              <div className="mt-auto pb-6" data-id="element-3002">
                {/* Summary */}
                {numAmount > 0 && isResolved && <div className="bg-gray-100 rounded-2xl p-4 mb-6" data-id="element-3003">
                    <div className="flex justify-between text-sm mb-2" data-id="element-3004">
                      <span className="text-gray-500 font-medium" data-id="element-3005">Amount</span>
                      <span className="font-bold text-[#0D3B3B]" data-id="element-3006">
                        {formatNaira(numAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mb-3 pb-3 border-b border-gray-200" data-id="element-3007">
                      <span className="text-gray-500 font-medium" data-id="element-3008">
                        Transfer Fee
                      </span>
                      <span className="font-bold text-[#0D3B3B]" data-id="element-3009">
                        {formatNaira(fee)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-3010">
                      <span className="font-bold text-[#0D3B3B]" data-id="element-3011">
                        Total Debit
                      </span>
                      <span className="font-bold text-lg text-[#0D3B3B]" data-id="element-3012">
                        {formatNaira(totalDebit)}
                      </span>
                    </div>
                  </div>}

                <button disabled={numAmount === 0 || !isResolved || numAmount > balance} onClick={() => setStep('confirm')} className={`w-full py-4 rounded-xl font-bold text-lg transition-all active:scale-95 ${numAmount > 0 && isResolved && numAmount <= balance ? 'bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3013">
                  {numAmount > balance ? 'Insufficient Funds' : 'Continue'}
                </button>
              </div>
            </motion.div>}

          {step === 'confirm' && <motion.div key="confirm" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-4 flex flex-col" data-id="element-3014">
              <div className="text-center mb-8 mt-4" data-id="element-3015">
                <p className="text-sm text-gray-500 font-medium mb-2" data-id="element-3016">
                  You are sending
                </p>
                <h1 className="text-5xl font-bold text-[#0D3B3B] tracking-tight mb-2" data-id="element-3017">
                  {formatNaira(numAmount)}
                </h1>
                <p className="text-sm text-gray-500" data-id="element-3018">to {recipientName}</p>
              </div>

              <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-3019">
                <div className="space-y-4" data-id="element-3020">
                  <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-3021">
                    <span className="text-sm text-gray-500" data-id="element-3022">Bank</span>
                    <span className="text-sm font-bold text-[#0D3B3B] uppercase" data-id="element-3023">
                      {bank}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-3024">
                    <span className="text-sm text-gray-500" data-id="element-3025">
                      Account Number
                    </span>
                    <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-3026">
                      {accountNo}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-3027">
                    <span className="text-sm text-gray-500" data-id="element-3028">Account Name</span>
                    <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-3029">
                      {recipientName}
                    </span>
                  </div>
                  {narration && <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-3030">
                      <span className="text-sm text-gray-500" data-id="element-3031">Narration</span>
                      <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-3032">
                        {narration}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center pb-4 border-b border-gray-50" data-id="element-3033">
                    <span className="text-sm text-gray-500" data-id="element-3034">Transfer Fee</span>
                    <span className="text-sm font-bold text-[#0D3B3B]" data-id="element-3035">
                      {formatNaira(fee)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2" data-id="element-3036">
                    <span className="text-base font-bold text-[#0D3B3B]" data-id="element-3037">
                      Total Debit
                    </span>
                    <span className="text-lg font-bold text-amber-600" data-id="element-3038">
                      {formatNaira(totalDebit)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-auto pb-6" data-id="element-3039">
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6" data-id="element-3040">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-500" data-id="element-3041" />
                  Secured by Skydd
                </div>
                <button onClick={() => setStep('pin')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#0D3B3B] hover:bg-[#092D2D] text-white shadow-lg shadow-emerald-900/20 transition-all active:scale-95" data-id="element-3042">
                  Confirm Transfer
                </button>
              </div>
            </motion.div>}

          {step === 'pin' && <motion.div key="pin" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="absolute inset-0 pb-safe px-5 pt-8 flex flex-col" data-id="element-3043">
              <div className="text-center mb-12" data-id="element-3044">
                <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-3045">
                  Enter Transaction PIN
                </h2>
                <p className="text-sm text-gray-500" data-id="element-3046">
                  Please enter your 4-digit PIN to authorize this transfer.
                </p>
              </div>

              {/* PIN Dots */}
              <div className="flex justify-center gap-6 mb-16" data-id="element-3047">
                {[0, 1, 2, 3].map(i => <div key={i} className={`w-4 h-4 rounded-full transition-all duration-300 ${i < pin.length ? 'bg-[#0D3B3B] scale-110' : 'bg-gray-200'}`} data-id="element-3048" />)}
              </div>

              {/* Number Pad */}
              <div className="mt-auto pb-8 max-w-[320px] mx-auto w-full" data-id="element-3049">
                <div className="grid grid-cols-3 gap-y-6 gap-x-8" data-id="element-3050">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => handlePinPress(num.toString())} className="h-16 rounded-full flex items-center justify-center text-2xl font-medium text-[#0D3B3B] hover:bg-gray-100 active:bg-gray-200 transition-colors" data-id="element-3051">
                      {num}
                    </button>)}
                  <div data-id="element-3052" /> {/* Empty cell */}
                  <button onClick={() => handlePinPress('0')} className="h-16 rounded-full flex items-center justify-center text-2xl font-medium text-[#0D3B3B] hover:bg-gray-100 active:bg-gray-200 transition-colors" data-id="element-3053">
                    0
                  </button>
                  <button onClick={handleDeletePin} className="h-16 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors" data-id="element-3054">
                    <DeleteIcon className="w-6 h-6" data-id="element-3055" />
                  </button>
                </div>
              </div>
            </motion.div>}

          {step === 'processing' && <motion.div key="processing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50" data-id="element-3056">
              <motion.div animate={{
            rotate: 360
          }} transition={{
            repeat: Infinity,
            duration: 1,
            ease: 'linear'
          }} className="mb-6" data-id="element-3057">
                <Loader2Icon className="w-12 h-12 text-amber-500" data-id="element-3058" />
              </motion.div>
              <h2 className="text-xl font-bold text-[#0D3B3B] mb-2" data-id="element-3059">
                Processing Transfer
              </h2>
              <p className="text-sm text-gray-500" data-id="element-3060">
                Please wait while we securely process your transaction...
              </p>
            </motion.div>}

          {step === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="absolute inset-0 overflow-y-auto pb-safe px-5 pt-10 flex flex-col bg-gradient-to-b from-[#06332E] via-[#06332E] to-[#0a4a43]" data-id="element-3061">
              <div className="text-center mb-8" data-id="element-3062">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              damping: 15
            }} className="mx-auto mb-4" data-id="element-3063">
                  <img src="https://cdn.magicpatterns.com/uploads/7UEvgvkXpEAQMdiRCwr1Yk/Layer_21.png" alt="Success mascot" className="w-48 h-48 object-contain mx-auto drop-shadow-2xl" data-id="element-3064" />
                </motion.div>

                <h1 className="text-2xl font-bold text-white mb-2" data-id="element-3065">
                  Transfer Successful!
                </h1>
                <p className="text-sm text-white/60 mb-6" data-id="element-3066">
                  Your money is on its way to {recipientName}.
                </p>

                <h2 className="text-4xl font-bold text-white tracking-tight" data-id="element-3067">
                  {formatNaira(numAmount)}
                </h2>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 border border-white/10 mb-8 relative overflow-hidden" data-id="element-3068">
                {/* Receipt jagged edge effect at top */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPjxwb2x5Z29uIHBvaW50cz0iMCwwIDQsNCA4LDAgOCw4IDAsOCIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIvPjwvc3ZnPg==')] bg-repeat-x" data-id="element-3069"></div>

                <div className="space-y-4 mt-2" data-id="element-3070">
                  <div className="flex justify-between items-center pb-4 border-b border-white/10 border-dashed" data-id="element-3071">
                    <span className="text-sm text-white/50" data-id="element-3072">Date & Time</span>
                    <span className="text-sm font-bold text-white" data-id="element-3073">
                      {formatDate(new Date().toISOString())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-white/10 border-dashed" data-id="element-3074">
                    <span className="text-sm text-white/50" data-id="element-3075">Reference</span>
                    <span className="text-sm font-bold text-white" data-id="element-3076">
                      TRX-{Math.floor(Math.random() * 1000000000)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-4 border-b border-white/10 border-dashed" data-id="element-3077">
                    <span className="text-sm text-white/50" data-id="element-3078">Recipient</span>
                    <div className="text-right" data-id="element-3079">
                      <div className="text-sm font-bold text-white" data-id="element-3080">
                        {recipientName}
                      </div>
                      <div className="text-xs text-white/40 uppercase" data-id="element-3081">
                        {bank} - {accountNo}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3082">
                    <span className="text-sm text-white/50" data-id="element-3083">Fee</span>
                    <span className="text-sm font-bold text-white" data-id="element-3084">
                      {formatNaira(fee)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-auto pb-6 space-y-3" data-id="element-3085">
                <button className="w-full py-4 rounded-xl font-bold text-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white shadow-sm flex items-center justify-center gap-2 transition-all active:scale-95 hover:bg-white/15" data-id="element-3086">
                  <ShareIcon className="w-5 h-5" data-id="element-3087" /> Share Receipt
                </button>
                <button onClick={() => navigate('wallet')} className="w-full py-4 rounded-xl font-bold text-lg bg-[#FFD700] text-[#06332E] shadow-lg shadow-black/20 transition-all active:scale-95 hover:bg-[#FFE033]" data-id="element-3088">
                  Back to Wallet
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}